import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { PaginationComponent } from 'src/app/shared/component/pagination/pagination.component';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';
import { DateInputComponent } from 'src/app/shared/component/date-input/date-input.component';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { ApiService } from 'src/app/services/api.service';
import { Urls } from 'src/app/lib/urls';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { RefreshService } from 'src/app/refresh.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import * as vo from 'src/app/core/model/server/vo/_index';

@Component({
  selector: 'app-admin-tp-cash-in',
  templateUrl: './admin-tp-cash-in.component.html',
  styleUrls: ['./admin-tp-cash-in.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    CommonModule,
    SharedComponentsModule,
    DateInputComponent,
    MegapayLoaderComponent,
    NgxSpinnerModule,
    PaginationComponent,
    TableControlsComponent,
    MatTooltip
  ]
})
export class AdminTpCashInComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public form: UntypedFormGroup;
  public limitForm: UntypedFormGroup;
  public searchForm: UntypedFormGroup;

  isLoading = false;
  submitted = false;
  showTable = false;
  fileName: any;
  params: any = {};

  // Data
  logsList: any[] = [];

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;

  // Search
  search_keyword = '';

  // Sorting
  sort_column = 'createDate';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // Filter fields
  filterFields: any = null;

  // ─── Active state getters ──────────────────────────────
  get activeList() { return this.logsList; }

  get activeLimit() {
    return {
      size:          this.size,
      total:         this.total ?? 0,
      onLimitChange: (e: any) => this.onLimitChangeFromComponent(e),
    };
  }

  get activeSearch() {
    return {
      onSearchKeyup: (e: any) => this.onSearchKeyupFromComponent(e),
    };
  }

  get activePage() {
    return {
      current: this.current_page ?? 1,
      total:   this.total_pages ?? 1,
    };
  }

  private resetTableState(): void {
    this.size = 10;
    this.form?.reset();
    this.limitForm?.reset({ limit: 10 });
    this.searchForm?.reset();
    this.search_keyword = '';
    this.total_pages = 0;
    this.filterFields = null;
    this.showTable = false;
    this.submitted = false;
    this.logsList = [];
    this.sort_column = 'createDate';
    this.sort_type = 'ASC';
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private apiService: ApiService,
    private refreshService: RefreshService,
    public paginationHelper: PaginationHelperService,
    private request: RequestsService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Fetch all logs on page load with empty filters
    this.submitted = true;
    this.filterFields = {};
    //this.get_logs_list();

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetTableState();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId: [null],
      dateFrom:      [null],
      dateTo:        [null],
    });

    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [10] });
  }

  // ─── Submit ────────────────────────────────────────────
  submit(): void {
    if (!this.form.valid) return;

    this.submitted = true;
    this.showTable = true;
    this.current_page = 1;
    this.search_keyword = '';

    // Build and store filter fields
    this.filterFields = {
      transactionId: this.form.controls.transactionId.value,
      dateFrom:      this.form.controls.dateFrom.value,
      dateTo:        this.form.controls.dateTo.value,
    };

    localStorage.setItem('itemLists', JSON.stringify(this.filterFields));

    this.get_logs_list();
    this.form.reset();
  }

  // ─── Data fetching ─────────────────────────────────────
  get_logs_list(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    const params: vo.CashInLogsRequest = {
      transactionId: this.filterFields?.transactionId,
      dateFrom:      this.filterFields?.dateFrom,
      dateTo:        this.filterFields?.dateTo,
      search:        this.search_keyword || undefined,
      sortBy: { [this.sort_column]: this.sort_type }
    };

    this.pages_array = [];

    this.apiService.getCashInLogs(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.logsList = response.result.data;
            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );

            this.total_pages = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total = parseInt(response.result.totalItems.toString(), 10);

          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error.message || error}`,
            'Failed Fetching Cash In Logs'
          );
          this.logsList = [];
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }
      });
  }

  // ─── Limit & Search handlers ───────────────────────────
  onLimitChangeFromComponent(event: any): void {
    const value = parseInt(event.target.value, 10);
    this.size = (!value || value <= 0) ? 10 : value;
    this.current_page = 1;
    this.get_logs_list();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.search_keyword = event.target.value;
    this.current_page = 1;
    this.get_logs_list();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.current_page = 1;
      this.get_logs_list();
    }
  }

  // ─── Sort ──────────────────────────────────────────────
  sortList(sort_column: string): void {
    if (!this.logsList?.length) return;
    this.sort_type = (this.sort_column === sort_column && this.sort_type === 'DESC') ? 'ASC' : 'DESC';
    this.sort_column = sort_column;
    this.current_page = 1;
    this.get_logs_list();
  }

  // ─── Pagination ────────────────────────────────────────
  setPage(num: number): void {
    this.current_page = num;
    this.get_logs_list();
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      this.get_logs_list();
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_logs_list();
    }
  }

  // ─── Export ────────────────────────────────────────────
  async print(): Promise<void> {
    this.spinner.show().catch((error) => { console.log(error); });
    const current = new Date();
    this.fileName = current.toLocaleDateString() + '-thirdparty-cashin-logs.xlsx';
    const exportParams = {
      ...this.filterFields,
      search: this.search_keyword || undefined,
    };


    try {
      await this.request.postBlob(Urls.exportCashInLogs, exportParams).then((report) => {
        const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = data;
        link.download = this.fileName;
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
      this.spinner.hide().catch((error) => { console.log(error); });
    } catch (error) {
      console.log('error export logs', error);
      this.spinner.hide().catch((err) => { console.log(err); });
    }
  }
}
